import React, { useState, useEffect } from "react";
import { doc, getDoc, updateDoc, collection, setDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebase"; // Adjust the path according to your firebase setup
import NavBars from '../../Sidebar/Sidebar';

const Tasks = () => {
  const [tasks, setTasks] = useState([]);
  const [subUnitRef, setSubUnitRef] = useState(null);  // Store the sub-unit reference
  const [subUnitName, setSubUnitName] = useState(""); // Store the sub-unit name
  const [progress, setProgress] = useState(0);

  // Fetch the subUnitRef and subUnitName from Firestore
  useEffect(() => {
    const fetchedSubUnitRef = JSON.parse(localStorage.getItem("subUnit"));
    if (fetchedSubUnitRef) {
      const subUnitReference = doc(db, "sub-units", fetchedSubUnitRef);
      setSubUnitRef(subUnitReference);

      const fetchSubUnitData = async () => {
        try {
          const subUnitDoc = await getDoc(subUnitReference);
          if (subUnitDoc.exists()) {
            const subUnitData = subUnitDoc.data();
            setSubUnitName(subUnitData.name); // Set the sub-unit name
          }
        } catch (error) {
          console.error("Error fetching sub-unit data:", error);
        }
      };

      fetchSubUnitData();
    } else {
      console.error("No sub-unit reference found in localStorage.");
    }
  }, []);

  // Fetch tasks when the subUnitRef is set
  useEffect(() => {
    if (!subUnitRef) {
      console.error("SubUnitRef is not set.");
      return;
    }

    const fetchTasks = async () => {
      try {
        const subUnitDoc = await getDoc(subUnitRef);  // Use subUnitRef directly
        if (subUnitDoc.exists()) {
          const subUnitData = subUnitDoc.data();

          // Ensure the tasks array exists or initialize it
          if (!subUnitData.tasks || !Array.isArray(subUnitData.tasks)) {
            // Initialize tasks array if it does not exist
            await updateDoc(subUnitRef, {
              tasks: []  // Create empty tasks array
            });
            setTasks([]);  // Initialize tasks state
          } else {
            setTasks(subUnitData.tasks); // Set tasks from Firestore
          }

          calculateProgress(subUnitData.tasks || []);  // Calculate progress based on tasks
        }
      } catch (error) {
        console.error("Error fetching tasks:", error);
      }
    };

    fetchTasks();
  }, [subUnitRef]);

  // Calculate progress percentage based on completed tasks
  const calculateProgress = (tasksArray) => {
    const totalTasks = tasksArray.length;
    const completedTasks = tasksArray.filter((task) => task.status === "completed").length;
  
    const currentDate = new Date();
    let totalDuration = 0;
    let elapsedDuration = 0;
  
    tasksArray.forEach((task) => {
      const startDate = new Date(task.startDate);
      const endDate = new Date(task.endDate);
  
      totalDuration += endDate - startDate;
  
      if (task.status === "completed") {
        elapsedDuration += endDate - startDate;
      } 
    });
  
    // Ensure totalDuration is not zero before calculating progress
    const completionProgress = totalDuration <= 0 || isNaN(totalDuration) ? 0 : (elapsedDuration / totalDuration) * 100;

  
    // Make sure the progress is a valid number
    setProgress(isNaN(completionProgress) ? 0 : completionProgress);
  };

  // Handle checkbox change to mark task as completed or incomplete
  const handleCheckboxChange = async (taskId, checked) => {
    try {
      const taskIndex = tasks.findIndex((task) => task.id === taskId);
      const updatedTasks = [...tasks];
      updatedTasks[taskIndex].status = checked ? "completed" : "incomplete";
      
      // Move completed tasks to the bottom
      const sortedTasks = [
        ...updatedTasks.filter(task => task.status !== "completed"),
        ...updatedTasks.filter(task => task.status === "completed")
      ];

      setTasks(sortedTasks);
      await updateDoc(subUnitRef, { tasks: sortedTasks }); // Update tasks in sub-unit
      calculateProgress(sortedTasks);  // Recalculate progress
    } catch (error) {
      console.error("Error updating task status:", error);
    }
  };

  // Handle input changes for task fields (name, description)
  const handleInputChange = async (taskId, field, value) => {
    try {
      const taskIndex = tasks.findIndex((task) => task.id === taskId);
      const updatedTasks = [...tasks];
      updatedTasks[taskIndex][field] = value;
      setTasks(updatedTasks);

      await updateDoc(subUnitRef, { tasks: updatedTasks }); // Update tasks in sub-unit
    } catch (error) {
      console.error(`Error updating task ${field}:`, error);
    }
  };

  // Handle adding a new task and creating a task document in the tasks collection
  const handleAddNewTask = async () => {
    if (!subUnitRef) {
      console.error("SubUnitRef is invalid.");
      return;
    }
  
    const newTask = {
      id: Date.now().toString(),
      name: "",
      description: "",
      status: "incomplete",
      startDate: "",
      endDate: "",
    };
  
    // Create a new task document in the "tasks" collection
    const taskRef = doc(collection(db, "tasks"));
    await setDoc(taskRef, {
      name: newTask.name,
      description: newTask.description,
      status: newTask.status,
      startDate: newTask.startDate,
      endDate: newTask.endDate,
      createdAt: serverTimestamp(),
    });
  
    // Add the reference of the newly created task to the sub-unit's tasks array
    const updatedTasks = [...tasks, { ...newTask, taskRef }];
    setTasks(updatedTasks);
  
    // Ensure subUnitRef is a valid reference
    if (subUnitRef) {
      await updateDoc(subUnitRef, { tasks: updatedTasks }); // Update tasks in sub-unit
    }
  };

  // Handle removing a task and its reference
  const handleRemoveTask = async (taskId) => {
    const taskToRemove = tasks.find((task) => task.id === taskId);
    if (taskToRemove?.taskRef) {
      // Remove the task document from the "tasks" collection
      await deleteDoc(taskToRemove.taskRef);
    }

    const updatedTasks = tasks.filter((task) => task.id !== taskId);
    setTasks(updatedTasks);

    await updateDoc(subUnitRef, { tasks: updatedTasks }); // Update tasks in sub-unit
  };

  // Ensure the start date is earlier than the end date
  const handleDateChange = (taskId, field, value) => {
    if (field === "startDate" || field === "endDate") {
      const updatedTasks = [...tasks];
      const taskIndex = updatedTasks.findIndex((task) => task.id === taskId);
      updatedTasks[taskIndex][field] = value;

      // If start date is greater than end date, revert change
      if (new Date(updatedTasks[taskIndex].startDate) > new Date(updatedTasks[taskIndex].endDate)) {
        alert("Start date cannot be later than end date.");
        return;
      }

      setTasks(updatedTasks);
      updateDoc(subUnitRef, { tasks: updatedTasks }); // Update tasks in sub-unit
    }
  };

  return (
    <div>
      <div className="content">
        <h2>Tasks for {subUnitName || "Sub-Unit"} Sub-Unit</h2>
        {/* Progress Bar */}
        <div className="progress-bar-container">
          <progress value={progress} max="100" />
          <span>{progress.toFixed(2)}% Complete</span>
        </div>

        {/* List of tasks */}
        <div className="task-list">
          {tasks.map((task) => (
            <div key={task.id} className="task-item">
              <input
                type="checkbox"
                checked={task.status === "completed"}
                onChange={(e) => handleCheckboxChange(task.id, e.target.checked)}
              />
              <div>
                <label>Task Name:</label>
                <input
                  type="text"
                  value={task.name}
                  onChange={(e) => handleInputChange(task.id, "name", e.target.value)}
                  placeholder="Task Name"
                />
              </div>
              <div>
                <label>Task Description:</label>
                <input
                  type="text"
                  value={task.description}
                  onChange={(e) => handleInputChange(task.id, "description", e.target.value)}
                  placeholder="Task Description"
                />
              </div>
              <div>
                <label>Start Date:</label>
                <input
                  type="date"
                  value={task.startDate}
                  onChange={(e) => handleDateChange(task.id, "startDate", e.target.value)}
                />
              </div>
              <div>
                <label>End Date:</label>
                <input
                  type="date"
                  value={task.endDate}
                  onChange={(e) => handleDateChange(task.id, "endDate", e.target.value)}
                />
              </div>
              <button onClick={() => handleRemoveTask(task.id)}>Remove</button>
            </div>
          ))}

          {/* Button to add new task */}
          <div className="add-task-button-container">
            <button className="add-task-button" onClick={handleAddNewTask}>Add New Task</button>
          </div>
        </div>
      </div>
      <NavBars />
    </div>
  );
};

export default Tasks;
